import React, { useEffect, useState, useMemo } from "react";
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  Select,
  message,
  Space,
  InputNumber,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
} from "@ant-design/icons";
import ProjectService from "../../../services/ProjectService";

const { Option } = Select;

// Status mapping with English - Marathi names
const STATUS_OPTIONS = [
  { value: "under_review", label: "Under Review - पुनरावलोकनाधीन" },
  { value: "planning_stage", label: "Planning Stage - नियोजन टप्पा" },
  { value: "awaiting_approval", label: "Awaiting Approval - मंजुरी प्रतीक्षा" },
  { value: "budget_review", label: "Budget Review - बजेट पुनरावलोकन" },
  { value: "site_survey", label: "Site Survey - साइट सर्वेक्षण" },
  {
    value: "feasibility_study",
    label: "Feasibility Study - व्यवहार्यता अभ्यास",
  },
  { value: "proposed", label: "Proposed - प्रस्तावित" },
  { value: "ongoing", label: "Ongoing - सुरू" },
  { value: "completed", label: "Completed - पूर्ण" },
];

// Group labels with bilingual names
const GROUP_LABELS = {
  ongoing: "Ongoing - सुरू",
  completed: "Completed - पूर्ण",
  proposed: "Proposed - प्रस्तावित",
};

// Helper to get status label
const getStatusLabel = (statusValue) => {
  const status = STATUS_OPTIONS.find((s) => s.value === statusValue);
  return status ? status.label : statusValue || "-";
};

const Projects = () => {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [viewing, setViewing] = useState(null);
  const [isViewOpen, setIsViewOpen] = useState(false);
  const [form] = Form.useForm();

  const fetch = async () => {
    setLoading(true);
    try {
      const res = await ProjectService.getProjects();
      console.log(res.data);
      setList(res.data || []);
    } catch (err) {
      console.error(err);
      message.error("Failed to load projects");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetch();
  }, []);

  // Group data by status: ongoing, completed, proposed (everything else)
  const groupedData = useMemo(() => {
    const groups = {
      ongoing: [],
      completed: [],
      proposed: [], // Everything that is NOT ongoing and NOT completed
    };

    list.forEach((item) => {
      if (item.status === "ongoing") {
        groups.ongoing.push(item);
      } else if (item.status === "completed") {
        groups.completed.push(item);
      } else {
        // All other statuses go to "proposed" group
        groups.proposed.push(item);
      }
    });

    // Sort each group by order (ascending)
    const sortByOrder = (a, b) => (a.order || 0) - (b.order || 0);
    groups.ongoing.sort(sortByOrder);
    groups.completed.sort(sortByOrder);
    groups.proposed.sort(sortByOrder);

    // Return groups in specific order: Ongoing, Completed, Proposed
    return [
      {
        key: "ongoing",
        groupName: GROUP_LABELS.ongoing,
        items: groups.ongoing,
        count: groups.ongoing.length,
      },
      {
        key: "completed",
        groupName: GROUP_LABELS.completed,
        items: groups.completed,
        count: groups.completed.length,
      },
      {
        key: "proposed",
        groupName: GROUP_LABELS.proposed,
        items: groups.proposed,
        count: groups.proposed.length,
      },
    ].filter((group) => group.items.length > 0); // Only show groups with items
  }, [list]);

  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({ language: "Marathi" });
    setIsModalOpen(true);
  };

  const openEdit = (record) => {
    setEditing(record);
    form.setFieldsValue({
      title: record.title,
      progress: record.progress,
      status: record.status,
      order: record.order,
      language: record.language || "Marathi",
    });
    setIsModalOpen(true);
  };

  const handleDelete = (id) => {
    Modal.confirm({
      title: "Delete project",
      content: "Are you sure?",
      async onOk() {
        try {
          await ProjectService.deleteProject(id);
          message.success("Deleted");
          fetch();
        } catch (err) {
          console.error(err);
          message.error("Delete failed");
        }
      },
    });
  };

  const handleView = (record) => {
    setViewing(record);
    setIsViewOpen(true);
  };

  const onFinish = async (values) => {
    console.log("Form values:", values);
    const payload = {
      title: values.title,
      progress: values.progress || 0,
      status: values.status || "",
      order: values.order || 0,
      language: values.language || "Marathi",
    };
    console.log("Payload being sent:", payload);

    setLoading(true);
    try { 
      if (editing) {
        await ProjectService.updateProject(editing.id, payload);
        message.success("Updated");
      } else {
        await ProjectService.createProject(payload);
        message.success("Created");
      }

      setIsModalOpen(false);
      form.resetFields();
      fetch();
    } catch (err) {
      console.error(err);
      message.error("Save failed");
    } finally {
      setLoading(false);
    }
  };

  const handleModalCancel = () => {
    setIsModalOpen(false);
    form.resetFields();
  };

  const columns = [
    {
      title: "Title",
      dataIndex: "title",
      key: "title",
      render: (t) => (
        <div style={{ maxWidth: 300, whiteSpace: "normal" }}>{t}</div>
      ),
    },
    {
      title: "Progress",
      dataIndex: "progress",
      key: "progress",
      render: (p) => <div>{p}%</div>,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (s) => getStatusLabel(s),
    },
    {
      title: "Language",
      dataIndex: "language",
      key: "language",
    },
    {
      title: "Order",
      dataIndex: "order",
      key: "order",
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, r) => (
        <Space>
          <Button
            icon={<EyeOutlined />}
            size="small"
            onClick={() => handleView(r)}
          >
            View
          </Button>
          <Button
            icon={<EditOutlined />}
            size="small"
            onClick={() => openEdit(r)}
          >
            Edit
          </Button>
          <Button
            danger
            icon={<DeleteOutlined />}
            size="small"
            onClick={() => handleDelete(r.id)}
          >
            Delete
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: 16 }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          marginBottom: 12,
          gap: 12,
        }}
      >
        <h3 style={{ margin: 0 }}>Projects (प्रकल्प)</h3>
        <div style={{ flexShrink: 0 }}>
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
            Add
          </Button>
        </div>
      </div>

      {groupedData.length === 0 ? (
        <Table columns={columns} dataSource={[]} loading={loading} />
      ) : (
        groupedData.map((group) => (
          <div key={group.key} style={{ marginBottom: 24 }}>
            <div
              style={{
                background: "linear-gradient(90deg, #001529 0%, #042035 100%)",
                padding: "12px 16px",
                borderRadius: "8px 8px 0 0",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <span style={{ color: "white", fontWeight: 600, fontSize: 16 }}>
                {group.groupName}
              </span>
              <span style={{ color: "rgba(255,255,255,0.9)", fontSize: 14 }}>
                Total Projects: {group.count}
              </span>
            </div>
            <Table
              columns={columns}
              dataSource={group.items}
              rowKey="id"
              loading={loading}
              pagination={false}
              size="small"
              style={{ borderRadius: "0 0 8px 8px", overflow: "hidden" }}
            />
          </div>
        ))
      )}

      <Modal
        title={editing ? "Edit Project" : "Add Project"}
        open={isModalOpen}
        onCancel={handleModalCancel}
        footer={null}
        width={500}
      >
        <Form form={form} layout="vertical" onFinish={onFinish}>
          <Form.Item
            name="title"
            label="Title (शीर्षक)"
            rules={[{ required: true, message: "Enter title" }]}
          >
            <Input placeholder="Enter project title" />
          </Form.Item>

          <Form.Item name="progress" label="Progress (प्रगती %)">
            <InputNumber
              min={0}
              max={100}
              style={{ width: "100%" }}
              placeholder="0-100"
            />
          </Form.Item>

          <Form.Item name="order" label="Order (क्रम)">
            <InputNumber
              min={0}
              style={{ width: "100%" }}
              placeholder="Display order"
            />
          </Form.Item>

          <Form.Item name="status" label="Status (स्थिती)">
            <Select
              allowClear
              showSearch
              placeholder="Select status"
              optionFilterProp="children"
            >
              {STATUS_OPTIONS.map((s) => (
                <Option key={s.value} value={s.value}>
                  {s.label}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="language"
            label="Language (भाषा)"
            initialValue="Marathi"
          >
            <Select>
              <Option value="English">English</Option>
              <Option value="Marathi">Marathi</Option>
              <Option value="Hindi">Hindi</Option>
            </Select>
          </Form.Item>

          <Form.Item>
            <Button type="primary" htmlType="submit" block loading={loading}>
              {editing ? "Update" : "Create"}
            </Button>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Project Details"
        open={isViewOpen}
        onCancel={() => setIsViewOpen(false)}
        footer={[
          <Button key="close" onClick={() => setIsViewOpen(false)}>
            Close
          </Button>,
        ]}
      >
        {viewing && (
          <div>
            <p>
              <strong>Title (शीर्षक):</strong> {viewing.title}
            </p>
            <p>
              <strong>Progress (प्रगती):</strong> {viewing.progress}%
            </p>
            <p>
              <strong>Status (स्थिती):</strong> {getStatusLabel(viewing.status)}
            </p>
            <p>
              <strong>Language (भाषा):</strong> {viewing.language}
            </p>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default Projects;
